/**
 * An approved quote from the old builder, read-only.
 *
 * Migration 0061 moved every v1 quote that could move. What is left is quotes
 * with a `quote_approvals` row: the record of what a customer agreed to, with
 * `quote_option_id` naming the option they picked. Re-describing one in visits
 * would change what the audit log says was agreed, so they keep their shape —
 * and this page is the only thing that draws it.
 *
 * It is deliberately a page and not a builder. Nothing here edits: an approved
 * quote refuses every mutation anyway (§6.4 makes a change a new version, not
 * an edit), so the old builder's option tabs, line editors and add buttons
 * were controls whose only possible outcome was a 409. What is left is what
 * somebody opening one of these actually wants — what was quoted and what was
 * chosen. (Its *New version* went with folder 52: a job with an approved
 * quote takes no new quote — `docs/job-stages.md` §3.)
 *
 * When the last of these is superseded this file goes too, and `quote_options`
 * stops being read by anything at all.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { request } from '../lib/api.js'
import { PageBar } from '../frame/page-bar.js'
import { EmptyState, SectionLabel, Tag } from '../components/primitives.js'
import { cn } from '../lib/cn.js'
import { formatDollars } from '../lib/format.js'

interface Payload {
  quote: {
    id: string
    reference: string
    quoteVersion: number
    status: string
    jobId: string
    jobReference: string
    propertyName: string | null
    accountName: string | null
    total: string
  }
  options: { id: string; label: string; total: string }[]
  lines: {
    id: string
    quoteOptionId: string | null
    description: string
    qty: string
    unit: string
    rate: string
    amount: string
    isAddOn: boolean
  }[]
}

/* `me` stays in the signature `quote-route.tsx` calls with; nothing here reads it now. */
export function QuoteArchive(_props: { me: MeResponse }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void request<Payload>(`/quotes/${id}`)
      .then(setData)
      .catch((cause: unknown) => setError(
        cause instanceof Error ? cause.message : 'That quote could not be loaded.'))
  }, [id])

  if (error) {
    return (
      <EmptyState
        title="Not found"
        body={error}
        action={{ label: 'Back to jobs', onClick: () => navigate('/jobs') }}
      />
    )
  }
  if (!data) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  const { quote, options, lines } = data

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar
        title={`Quote ${quote.reference}`}
        breadcrumbs={[
          { label: 'Jobs', onClick: () => navigate('/jobs') },
          { label: quote.jobReference, onClick: () => navigate(`/jobs/${quote.jobId}`) },
        ]}
        sub={[quote.propertyName, quote.accountName].filter(Boolean).join(' · ')}
        status={<Tag tone="ok">
          {quote.status[0]!.toUpperCase() + quote.status.slice(1)} · v{quote.quoteVersion}
        </Tag>}
      />

      {/*
        * No **New version** (`docs/job-stages.md` §3): once a job has an
        * approved quote there is no new quote on it — a change is a change
        * order (folder 51) — and the legacy `/jobs/:id?newQuote=1` it opened
        * is gone with it (walkthrough findings §4).
        */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-bg px-6 py-4">
        <p className="m-0 mb-3.5 max-w-[640px] text-body text-text-muted">
          This quote was written in the earlier builder and has been approved, so it is
          kept exactly as the customer agreed to it.
        </p>

        <div className="flex flex-col gap-3.5">
          {options.map((option) => {
            const mine = lines.filter((line) => line.quoteOptionId === option.id
              || line.quoteOptionId === null)
            return (
              <section key={option.id}
                className="overflow-hidden rounded-record border border-line bg-surface">
                <header className="flex items-baseline gap-3 px-[18px] py-3">
                  <b className="min-w-0 flex-1 truncate text-[15px] font-bold text-text">
                    {option.label}
                  </b>
                  <b className="tnum shrink-0 text-[15px] font-bold text-text">
                    {formatDollars(Number(option.total))}
                  </b>
                </header>

                <table className="w-full border-t border-line text-cell">
                  <thead>
                    <tr className="bg-surface-alt">
                      <Th className="text-left">Line</Th>
                      <Th className="w-[110px] text-right">Qty</Th>
                      <Th className="w-[90px] text-left">Unit</Th>
                      <Th className="w-[110px] text-right">Rate</Th>
                      <Th className="w-[120px] text-right">Amount</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {mine.map((line) => (
                      <tr key={line.id} className="border-t border-line-soft">
                        <Td className="text-left">
                          {line.description}
                          {line.isAddOn && (
                            <span className="ml-2 text-[11px] text-info">add-on</span>
                          )}
                        </Td>
                        <Td className="tnum text-right">{line.qty}</Td>
                        <Td className="text-left text-text-muted">{line.unit}</Td>
                        <Td className="tnum text-right">{formatDollars(Number(line.rate))}</Td>
                        <Td className="tnum text-right font-semibold">
                          {formatDollars(Number(line.amount))}
                        </Td>
                      </tr>
                    ))}
                    {mine.length === 0 && (
                      <tr className="border-t border-line-soft">
                        <Td className="text-left text-text-faint">Nothing on this option.</Td>
                        <Td className="text-right" />
                        <Td className="text-left" />
                        <Td className="text-right" />
                        <Td className="text-right" />
                      </tr>
                    )}
                  </tbody>
                </table>
              </section>
            )
          })}

          {options.length === 0 && (
            <EmptyState
              title="Nothing on this quote"
              body="It was approved with no priced lines on it."
            />
          )}
        </div>

        <section className="mt-3.5 rounded-record border border-line bg-surface px-[18px] py-3.5">
          <SectionLabel className="mb-1.5 block">Approved total</SectionLabel>
          <b className="tnum text-[22px] font-bold tracking-[-0.01em] text-text">
            {formatDollars(Number(quote.total))}
          </b>
        </section>
      </div>
    </div>
  )
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={cn('px-[18px] py-2 text-[10.5px] font-semibold uppercase',
      'tracking-[0.05em] text-text-faint', className)}>
      {children}
    </th>
  )
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn('px-[18px] py-2.5 text-text', className)}>{children}</td>
}
